'use client'

import { useLanguage } from '@/lib/language-context'
import { getLocalizedPath } from '@/lib/seo'
import { supportedLanguages, languageNames, Language } from '@/lib/translations'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Languages } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { cn } from '@/lib/utils'

interface LanguageSwitcherProps {
  className?: string
  /** Unprefixed route (e.g. '/blog'). When set, switching navigates so server-rendered content reloads in the new language. */
  path?: string
}

export function LanguageSwitcher({ className, path }: LanguageSwitcherProps) {
  const { language, setLanguage } = useLanguage()
  const router = useRouter()

  const selectLanguage = (lang: Language) => {
    setLanguage(lang)
    if (path !== undefined) {
      router.push(getLocalizedPath(path, lang))
    }
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'relative text-[15px] font-medium tracking-[0.01em] subpixel-antialiased text-foreground hover:bg-accent/10 hover:text-foreground focus:bg-accent/10 focus:text-foreground',
            className
          )}
        >
          <Languages className="mr-2 h-[18px] w-[18px]" aria-hidden="true" />
          <span className="hidden sm:inline">{languageNames[language]}</span>
          <span className="sm:hidden">{language.toUpperCase()}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-w-[calc(100vw-2rem)] bg-popover border-border">
        {supportedLanguages.map((lang) => (
          <DropdownMenuItem
            key={lang}
            onSelect={() => selectLanguage(lang)}
            className={`cursor-pointer text-foreground hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground ${
              language === lang ? 'bg-accent/50 text-accent-foreground' : ''
            }`}
          >
            {languageNames[lang]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}